import {
  Customer,
  CustomerResp,
  QueryCustomersArgs,
  MutationUpdateCustomerArgs,
  MutationAddCustomerArgs
} from "@/types/__gql__"
import { useQuery, useMutation, ApolloError, gql, ApolloQueryResult } from "@apollo/client"

export const CUSTOMER_FRAGMENT = gql`
  fragment CustomerFragment on Customer {
    __typename
    _id
    customerName
    createdAt
    logoUrl
    region
    domainName
    submitConfirmationEmailConfig {
      enabled
      templateId
    }
    startInterviewEmailConfig {
      enabled
      templateId
    }
    firstTimeReminderEmailConfig {
      enabled
      templateId
    }
    secondTimeReminderEmailConfig {
      enabled
      templateId
    }
    ssoIdentityProviderConfig {
      enabled
      loginUrl
      publicCertificates
      shouldSignGetRequest
      shouldEncryptAssertions
    }
    theme {
      fiTitle
      bgColor
      textColor
      highlightColor
      header {
        backgroundColor
        fontColor
      }
      hint {
        backgroundColor
        fontColor
      }
      question {
        backgroundColor
        fontColor
      }
      response {
        backgroundColor
        fontColor
      }
      listItem {
        backgroundColor
        fontColor
      }
      selectedItem {
        backgroundColor
        fontColor
      }
      button {
        backgroundColor
        fontColor
      }
      progressBar {
        backgroundColor
        fontColor
      }
    }
  }
`

export const GET_CUSTOMERS = gql`
  query CustomersRes($customerName: String!, $continueToken: String) {
    customers(customerName: $customerName, continueToken: $continueToken) {
      data {
        ...CustomerFragment
      }
      continueToken
    }
  }
  ${CUSTOMER_FRAGMENT}
`

export const UPDATE_CUSTOMER = gql`
  mutation UpdateCustomerRes($customerId: String!, $update: UpdateCustomerInput!) {
    updateCustomer(customerId: $customerId, update: $update) {
      ...CustomerFragment
    }
  }
  ${CUSTOMER_FRAGMENT}
`

export const GET_REGIONS = gql`
  query UserReginosRes {
    regions
  }
`
export const ADD_CUSTOMER = gql`
  mutation AddCustomerRes($input: AddCustomerInput!) {
    addCustomer(input: $input) {
      _id
    }
  }
`

export const useCustomers = ({
  customerId,
  customerName
}: {
  customerId: string
  customerName: string
}): {
  data: Array<Customer>
  error: ApolloError
  loading: boolean
  hasMore: boolean
  fetchMore: (
    variables?: QueryCustomersArgs
  ) => Promise<
    ApolloQueryResult<{
      customers: CustomerResp
    }>
  >
  refetch: (
    variables?: QueryCustomersArgs
  ) => Promise<
    ApolloQueryResult<{
      customers: CustomerResp
    }>
  >
} => {
  const { data, loading, error, fetchMore, refetch } = useQuery<
    { customers: CustomerResp },
    QueryCustomersArgs
  >(GET_CUSTOMERS, {
    variables: { customerName: customerName || "", continueToken: "" },
    notifyOnNetworkStatusChange: true,
    errorPolicy: "all"
  })

  return {
    fetchMore: () => {
      return fetchMore({
        variables: {
          customerName: customerName || "",
          continueToken: (data && data.customers && data.customers.continueToken) || ""
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newList = fetchMoreResult.customers.data
          const continueToken = fetchMoreResult.customers.continueToken
          if (previousResult) {
            return newList.length
              ? {
                  customers: {
                    __typename: previousResult.customers.__typename,
                    data: [...previousResult.customers.data, ...newList],
                    continueToken
                  }
                }
              : previousResult
          }
        }
      })
    },
    loading,
    hasMore: !!(data && data.customers && data.customers.continueToken),
    error,
    data: data?.customers?.data,
    refetch
  }
}

export const useUpdateCustomer = ({ customerId }: { customerId: string }) => {
  const [updateCustomer] = useMutation<Customer, MutationUpdateCustomerArgs>(UPDATE_CUSTOMER, {
    errorPolicy: "all"
  })
  return { updateCustomer }
}

export const useRegions = ({
  customerId
}: {
  customerId: string
}): {
  res: {
    regions: Array<string>
  }
  error: ApolloError
  loading: boolean
} => {
  const { data, loading, error } = useQuery(GET_REGIONS, { errorPolicy: "all" })
  return {
    loading,
    error,
    res: data || {}
  }
}

export const useAddCustomer = ({
  customerId
}: {
  customerId: string
}): { addCustomer: Function } => {
  const [addCustomer] = useMutation<Customer, MutationAddCustomerArgs>(ADD_CUSTOMER, {
    errorPolicy: "all"
  })
  return { addCustomer }
}
